import { Chain, defineChain } from 'viem';
import { Chain as c } from '@rainbow-me/rainbowkit';

export const larissaChain = {
    id: Number(import.meta.env.VITE_CHAIN_ID || '9898'),
    name: 'Larissa Network',
    nativeCurrency: {
        decimals: 18,
        name: 'LRS',
        symbol: 'LRS',
    },
    rpcUrls: {
        default: {
            http: [import.meta.env.VITE_RPC_URL || 'https://rpc.larissa.network'],
        },
        public: {
            http: [import.meta.env.VITE_RPC_URL || 'https://rpc.larissa.network'],
        },
    },
    blockExplorers: {
        default: { name: 'LarissaScan', url: 'https://scan.larissa.network' },
    },
    testnet: false,
} as const satisfies Chain;

export const larissaChainRainbow = {
    id: larissaChain.id,
    name: larissaChain.name,
    iconUrl: '/images/logos/128x128.png',
    iconBackground: '#000000',
    nativeCurrency: {
        decimals: larissaChain.nativeCurrency.decimals,
        name: larissaChain.nativeCurrency.name,
        symbol: larissaChain.nativeCurrency.symbol,
    },
    rpcUrls: {
        default: {
            http: larissaChain.rpcUrls.default.http,
        },
        public: {
            http: larissaChain.rpcUrls.public.http,
        },
    },
    blockExplorers: {
        default: {
            name: larissaChain.blockExplorers.default.name,
            url: larissaChain.blockExplorers.default.url,
        },
    },
    testnet: larissaChain.testnet,
} as const satisfies c;

// Ethereum Mainnet configuration for bridge
export const ethereumForBridge = defineChain({
    id: 1,
    name: 'Ethereum',
    network: 'ethereum',
    nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
    },
    rpcUrls: {
        default: {
            http: ['https://eth.llamarpc.com'],
        },
        public: {
            http: ['https://eth.llamarpc.com'],
        },
    },
    blockExplorers: {
        default: {
            name: 'Etherscan',
            url: 'https://etherscan.io',
            apiUrl: 'https://api.etherscan.io/api'
        },
    },
    testnet: false,
})

// Polygon configuration for bridge
export const polygonForBridge = defineChain({
    id: 137,
    name: 'Polygon',
    network: 'polygon',
    nativeCurrency: {
        decimals: 18,
        name: 'MATIC',
        symbol: 'MATIC',
    },
    rpcUrls: {
        default: {
            http: ['https://1rpc.io/matic'],
        },
        public: {
            http: ['https://1rpc.io/matic'],
        },
    },
    blockExplorers: {
        default: {
            name: 'PolygonScan',
            url: 'https://polygonscan.com',
            apiUrl: 'https://api.polygonscan.com/api'
        },
    },
    testnet: false,
})

// Arbitrum One (for bridge / donations)
export const arbitrumForBridge = defineChain({
    id: 42161,
    name: 'Arbitrum One',
    network: 'arbitrum',
    nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
    },
    rpcUrls: {
        default: { http: ['https://arb1.arbitrum.io/rpc'] },
        public: { http: ['https://arb1.arbitrum.io/rpc'] },
    },
    blockExplorers: {
        default: {
            name: 'Arbiscan',
            url: 'https://arbiscan.io',
            apiUrl: 'https://api.arbiscan.io/api',
        },
    },
    testnet: false,
})

// Base (for donations)
export const baseForBridge = defineChain({
    id: 8453,
    name: 'Base',
    network: 'base',
    nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
    },
    rpcUrls: {
        default: { http: ['https://mainnet.base.org'] },
        public: { http: ['https://mainnet.base.org'] },
    },
    blockExplorers: {
        default: {
            name: 'Basescan',
            url: 'https://basescan.org',
            apiUrl: 'https://api.basescan.org/api',
        },
    },
    testnet: false,
})

// Optimism (for donations)
export const optimismForBridge = defineChain({
    id: 10,
    name: 'Optimism',
    network: 'optimism',
    nativeCurrency: {
        decimals: 18,
        name: 'Ether',
        symbol: 'ETH',
    },
    rpcUrls: {
        default: { http: ['https://mainnet.optimism.io'] },
        public: { http: ['https://mainnet.optimism.io'] },
    },
    blockExplorers: {
        default: {
            name: 'Optimistic Etherscan',
            url: 'https://optimistic.etherscan.io',
            apiUrl: 'https://api-optimistic.etherscan.io/api',
        },
    },
    testnet: false,
})

// Binance Smart Chain configuration for bridge
export const binanceForBridge = defineChain({
    id: 56,
    name: 'BNB Smart Chain',
    network: 'bsc',
    nativeCurrency: {
        decimals: 18,
        name: 'BNB',
        symbol: 'BNB',
    },
    rpcUrls: {
        default: {
            http: ['https://bsc-dataseed.bnbchain.org'],
        },
        public: {
            http: ['https://bsc-dataseed.bnbchain.org'],
        },
    },
    blockExplorers: {
        default: {
            name: 'BscScan',
            url: 'https://bscscan.com',
            apiUrl: 'https://api.bscscan.com/api'
        },
    },
    testnet: false,
})
